import React, { useState, useEffect, useCallback } from 'react';
import { Search, X } from 'lucide-react';
import BaseModal from './BaseModal';

const IconPicker = ({ isOpen, onClose, onSelect, selectedIcon }) => {
  const [iconSearchQuery, setIconSearchQuery] = useState('');
  const [searchedIcons, setSearchedIcons] = useState([]);
  const [isSearching, setIsSearching] = useState(false);

  // AI-powered icon search function
  const searchIcons = useCallback(async (query) => {
    if (!query.trim()) {
      setSearchedIcons([]);
      return;
    }

    setIsSearching(true);
    try {
      // Icon-to-keyword mapping for AI-like suggestions
      const iconMappings = {
        // Food & Dining
        '🍔': ['burger', 'hamburger', 'fast food', 'mcdonalds', 'food', 'eating', 'lunch', 'dinner'],
        '🍕': ['pizza', 'italian', 'food', 'slice', 'eating', 'dinner', 'restaurant'],
        '🌮': ['taco', 'mexican', 'food', 'spicy', 'lunch', 'dinner'],
        '🍝': ['pasta', 'spaghetti', 'italian', 'food', 'noodles', 'dinner'],
        '🍜': ['noodles', 'soup', 'ramen', 'asian', 'food', 'hot', 'lunch'],
        '☕': ['coffee', 'cafe', 'drink', 'morning', 'caffeine', 'hot', 'starbucks'],
        '🍺': ['beer', 'alcohol', 'drink', 'bar', 'party', 'social', 'brewery'],
        '🍷': ['wine', 'alcohol', 'drink', 'dinner', 'fancy', 'restaurant', 'red wine'],
        '🥛': ['milk', 'drink', 'dairy', 'breakfast', 'healthy', 'white'],
        '🧃': ['juice', 'drink', 'fruit', 'healthy', 'kids', 'breakfast'],

        // Transportation
        '🚗': ['car', 'drive', 'vehicle', 'auto', 'transport', 'road', 'commute', 'uber', 'lyft'],
        '🚕': ['taxi', 'cab', 'yellow', 'ride', 'transport', 'city', 'uber'],
        '🚌': ['bus', 'public transport', 'commute', 'city', 'transit'],
        '🚂': ['train', 'railway', 'commute', 'transport', 'travel'],
        '✈️': ['plane', 'airplane', 'flight', 'travel', 'vacation', 'airport', 'fly'],
        '🚲': ['bike', 'bicycle', 'cycle', 'exercise', 'green', 'transport'],
        '🏍️': ['motorcycle', 'bike', 'motor', 'fast', 'ride'],
        '⛽': ['gas', 'fuel', 'petrol', 'station', 'car', 'fill up', 'shell', 'gasoline'],
        '🚁': ['helicopter', 'chopper', 'fly', 'emergency', 'air'],

        // Shopping & Retail
        '🛒': ['shopping', 'cart', 'grocery', 'store', 'buy', 'retail', 'supermarket'],
        '🛍️': ['shopping', 'bags', 'retail', 'buy', 'store', 'mall'],
        '💳': ['credit card', 'payment', 'money', 'bank', 'finance', 'pay', 'card'],
        '💰': ['money', 'cash', 'wealth', 'finance', 'rich', 'salary', 'income'],
        '💸': ['money', 'cash', 'spending', 'expense', 'pay', 'flying money'],
        '🏪': ['store', 'shop', 'retail', 'business', 'convenience'],
        '🏬': ['mall', 'shopping center', 'retail', 'department store'],
        '💎': ['diamond', 'jewelry', 'expensive', 'luxury', 'valuable'],
        '👕': ['shirt', 'clothing', 'clothes', 'fashion', 'wear'],
        '👖': ['pants', 'jeans', 'clothing', 'clothes', 'fashion'],

        // Health & Medical
        '🏥': ['hospital', 'medical', 'health', 'doctor', 'emergency', 'clinic'],
        '💊': ['pill', 'medicine', 'pharmacy', 'drug', 'medication', 'health'],
        '💉': ['injection', 'vaccine', 'medical', 'doctor', 'shot', 'needle'],
        '🩺': ['stethoscope', 'doctor', 'medical', 'health', 'checkup'],
        '🦷': ['tooth', 'dental', 'dentist', 'teeth', 'oral health'],
        '🧬': ['dna', 'genetics', 'science', 'medical', 'biology'],
        '🔬': ['microscope', 'science', 'lab', 'research', 'medical'],
        '🌡️': ['thermometer', 'temperature', 'fever', 'sick', 'health'],

        // Home & Utilities
        '🏠': ['home', 'house', 'residence', 'property', 'real estate'],
        '💡': ['light', 'electricity', 'utilities', 'power', 'bright', 'idea'],
        '🔌': ['plug', 'electricity', 'power', 'utilities', 'outlet'],
        '🔋': ['battery', 'power', 'energy', 'charge', 'electric'],
        '📱': ['phone', 'mobile', 'smartphone', 'cell', 'iphone', 'android'],
        '💻': ['laptop', 'computer', 'work', 'tech', 'office'],
        '📺': ['tv', 'television', 'entertainment', 'watch', 'screen'],
        '⚡': ['lightning', 'electricity', 'power', 'energy', 'fast', 'danger'],
        '💧': ['water', 'utilities', 'drop', 'liquid', 'blue'],

        // Entertainment & Hobbies
        '🎬': ['movie', 'cinema', 'film', 'entertainment', 'theater'],
        '🎮': ['gaming', 'video games', 'play', 'console', 'xbox', 'playstation'],
        '🎨': ['art', 'paint', 'creative', 'design', 'hobby', 'artist'],
        '🎯': ['target', 'goal', 'aim', 'dartboard', 'precision'],
        '🎲': ['dice', 'game', 'gambling', 'chance', 'board game'],
        '🎸': ['guitar', 'music', 'instrument', 'rock', 'band'],
        '🎤': ['microphone', 'singing', 'music', 'performance', 'karaoke'],
        '📚': ['books', 'reading', 'study', 'library', 'education'],
        '🎭': ['theater', 'drama', 'acting', 'performance', 'masks'],

        // Work & Education
        '💼': ['briefcase', 'work', 'business', 'office', 'professional'],
        '📊': ['chart', 'graph', 'data', 'analytics', 'business', 'statistics'],
        '📈': ['trending up', 'growth', 'profit', 'success', 'increase'],
        '📉': ['trending down', 'loss', 'decrease', 'decline', 'chart'],
        '🎓': ['graduation', 'education', 'school', 'university', 'degree'],
        '📝': ['writing', 'notes', 'document', 'memo', 'text'],
        '🖊️': ['pen', 'writing', 'signature', 'ink'],
        '📋': ['clipboard', 'list', 'checklist', 'document'],

        // Finance & Banking
        '🏦': ['bank', 'banking', 'finance', 'money', 'institution'],
        '💹': ['stock market', 'trading', 'finance', 'investment', 'chart'],
        '💱': ['currency', 'exchange', 'money', 'foreign', 'forex'],
        '💲': ['dollar', 'money', 'currency', 'price', 'cost'],
        '🔒': ['lock', 'security', 'safe', 'protection', 'secure'],
        '🗝️': ['key', 'unlock', 'access', 'security', 'password'],

        // Nature & Weather
        '🌱': ['plant', 'growth', 'green', 'nature', 'seedling'],
        '🌳': ['tree', 'nature', 'environment', 'green', 'forest'],
        '🌹': ['rose', 'flower', 'love', 'romantic', 'red'],
        '☀️': ['sun', 'sunny', 'weather', 'bright', 'hot'],
        '🌧️': ['rain', 'weather', 'wet', 'storm'],
        '❄️': ['snow', 'cold', 'winter', 'ice', 'freeze'],

        // Animals & Pets
        '🐶': ['dog', 'puppy', 'pet', 'animal', 'cute'],
        '🐱': ['cat', 'kitten', 'pet', 'animal', 'cute'],
        '🐭': ['mouse', 'small', 'animal', 'rodent'],
        '🐻': ['bear', 'animal', 'wild', 'cute'],
        '🦁': ['lion', 'king', 'wild', 'animal', 'strong'],

        // Substances & Vices
        '🚬': ['cigarette', 'smoking', 'tobacco', 'smoke', 'nicotine', 'addiction'],
        '🍃': ['leaf', 'weed', 'marijuana', 'cannabis', 'green', 'plant'],

        // Hazard & Warning
        '⚠️': ['warning', 'hazard', 'caution', 'alert', 'danger', 'attention'],
        '☢️': ['radioactive', 'nuclear', 'hazard', 'danger', 'toxic'],
        '☣️': ['biohazard', 'biological', 'hazard', 'danger', 'toxic'],
        '🚫': ['prohibited', 'no', 'ban', 'forbidden', 'stop', 'not allowed'],
        '⛔': ['no entry', 'stop', 'prohibited', 'forbidden', 'blocked'],
        '🔥': ['fire', 'hot', 'trending', 'popular', 'burn', 'flame'],
        '💥': ['explosion', 'blast', 'boom', 'dangerous', 'impact'],
        '🔴': ['red', 'stop', 'danger', 'alert', 'warning'],
        '🔺': ['triangle', 'warning', 'caution', 'alert', 'up'],

        // Common symbols
        '⭐': ['star', 'favorite', 'rating', 'quality', 'excellent'],
        '❤️': ['heart', 'love', 'like', 'favorite', 'red'],
        '💯': ['hundred', 'perfect', 'score', 'complete', 'all'],
        '✨': ['sparkles', 'magic', 'special', 'shiny', 'stars'],
        '💫': ['dizzy', 'stars', 'magic', 'special', 'sparkle'],
        '📄': ['document', 'paper', 'file', 'text', 'page', 'other']
      };

      // Search for matching icons based on query
      const queryLower = query.toLowerCase();
      const matches = [];

      Object.entries(iconMappings).forEach(([icon, keywords]) => {
        const score = keywords.reduce((acc, keyword) => {
          if (keyword.includes(queryLower) || queryLower.includes(keyword)) {
            return acc + (keyword === queryLower ? 10 : keyword.startsWith(queryLower) ? 5 : 1);
          }
          return acc;
        }, 0);

        if (score > 0) {
          matches.push({ icon, score, keywords: keywords.slice(0, 3) });
        }
      });

      // Sort by relevance score and take top 20 results
      const sortedMatches = matches
        .sort((a, b) => b.score - a.score)
        .slice(0, 20)
        .map(match => match.icon);

      setSearchedIcons(sortedMatches);
    } catch (err) {
      console.error('Error searching icons:', err);
      setSearchedIcons([]);
    } finally {
      setIsSearching(false);
    }
  }, []);

  // Debounced search effect
  useEffect(() => {
    const timeoutId = setTimeout(() => {
      searchIcons(iconSearchQuery);
    }, 300);

    return () => clearTimeout(timeoutId);
  }, [iconSearchQuery, searchIcons]);

  useEffect(() => {
    const handleEscKey = (e) => {
      if (e.key === 'Escape' && isOpen) {
        onClose();
      }
    };

    if (isOpen) {
      document.addEventListener('keydown', handleEscKey);
    }

    return () => {
      document.removeEventListener('keydown', handleEscKey);
    };
  }, [isOpen, onClose]);

  const iconCategories = {
    'Popular': ['💰', '⛽', '🍔', '🛒', '🏠', '💡', '🚗', '✈️', '🎬', '💊', '🎯', '📱', '💳', '☕', '🎮', '📚'],
    'Finance': ['💰', '💳', '🏦', '💸', '💵', '💹', '💱', '💲', '📊', '📈', '📉', '🔒', '💎'],
    'Transportation': ['🚗', '🚕', '🚌', '🚂', '✈️', '🚲', '🏍️', '⛽', '🚁', '🚤', '🚢'],
    'Food & Dining': ['🍔', '🍕', '🌮', '🍝', '🍜', '☕', '🍺', '🍷', '🥛', '🧃'],
    'Shopping': ['🛒', '🛍️', '🏪', '🏬', '💎', '👕', '👖'],
    'Health': ['🏥', '💊', '💉', '🩺', '🦷', '🧬', '🔬', '🌡️'],
    'Home': ['🏠', '💡', '🔌', '🔋', '📱', '💻', '📺'],
    'Entertainment': ['🎬', '🎮', '🎨', '🎯', '🎲', '🎸', '🎤', '📚', '🎭'],
    'Work': ['💼', '📊', '📈', '🎓', '📝', '🖊️', '📋'],
    'Warning': ['⚠️', '☢️', '☣️', '🚫', '⛔', '🔥', '💥', '🔴'],
    'Nature': ['🌱', '🌳', '🌹', '☀️', '🌧️', '❄️'],
    'Animals': ['🐶', '🐱', '🐭', '🐻', '🦁']
  };

  const handleIconSelect = (icon) => {
    onSelect(icon);
    onClose();
  };

  return (
    <BaseModal
      isOpen={isOpen}
      onClose={onClose}
      size="2xl"
      showCloseButton={false}
      contentClassName="flex flex-col max-h-[80vh]"
    >
      {/* Header */}
      <div className="flex items-center justify-between p-4 border-b border-gray-200">
        <h3 className="text-lg font-medium text-gray-900">Choose Icon</h3>
        <button
          type="button"
          onClick={onClose}
          className="text-gray-400 hover:text-gray-600"
          aria-label="Close modal"
        >
          <X className="h-5 w-5" />
        </button>
      </div>

        {/* Search */}
        <div className="p-4 border-b border-gray-200">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
            <input
              type="text"
              value={iconSearchQuery}
              onChange={(e) => setIconSearchQuery(e.target.value)}
              placeholder="Search icons (e.g., gas, food, money, car)..."
              className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              autoFocus
            />
          </div>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-4">
          {/* Search Results */}
          {iconSearchQuery && (
            <div className="mb-6">
              <h4 className="text-sm font-medium text-gray-700 mb-3">
                {isSearching ? 'Searching...' : `Search Results (${searchedIcons.length})`}
              </h4>
              <div className="grid grid-cols-8 gap-2">
                {searchedIcons.map((icon, index) => (
                  <button
                    key={`search-${index}-${icon}`}
                    onClick={() => handleIconSelect(icon)}
                    className={`p-3 text-2xl rounded-lg hover:bg-gray-100 border-2 transition-colors ${
                      selectedIcon === icon ? 'border-blue-500 bg-blue-50' : 'border-gray-200'
                    }`}
                    title={icon}
                  >
                    {icon}
                  </button>
                ))}
              </div>
              {searchedIcons.length === 0 && !isSearching && (
                <div className="text-center py-8 text-gray-500">
                  No icons found for "{iconSearchQuery}". Try keywords like: gas, food, money, transport
                </div>
              )}
            </div>
          )}

          {/* Categories */}
          {!iconSearchQuery && (
            <div className="space-y-6">
              {Object.entries(iconCategories).map(([categoryName, icons]) => (
                <div key={categoryName}>
                  <h4 className="text-sm font-medium text-gray-700 mb-3">
                    {categoryName} ({icons.length})
                  </h4>
                  <div className="grid grid-cols-8 gap-2">
                    {icons.map((icon, index) => (
                      <button
                        key={`${categoryName}-${index}-${icon}`}
                        onClick={() => handleIconSelect(icon)}
                        className={`p-3 text-2xl rounded-lg hover:bg-gray-100 border-2 transition-colors ${
                          selectedIcon === icon ? 'border-blue-500 bg-blue-50' : 'border-gray-200'
                        }`}
                        title={icon}
                      >
                        {icon}
                      </button>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
    </BaseModal>
  );
};

export default IconPicker;